import { getAccount } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';
import type { CommonSuccessResponse } from './register';

export interface ForgotPasswordRequest {
  Email: string;
  AccountDomainId?: string | number;
}

export interface ForgotPasswordSmsRequest {
  Telephone: string;
  AccountDomainId?: string | number;
}

export interface ForgotPasswordSmsResponse extends CommonSuccessResponse {
  Guid?: string;
}

export interface ForgotUsernameRequest {
  Email: string;
  AccountDomainId?: string | number;
}

export interface ResetPasswordRequest {
  Guid: string;
  Password: string;
  ConfirmPassword: string;
  Code?: string;
}

function accountDomainId(
  explicit?: string | number,
): string | number | undefined {
  if (explicit !== undefined && explicit !== null && String(explicit).trim() !== '') {
    return explicit;
  }
  return getAccount()?.AccountDomainId;
}

/** Request a password-reset email. */
export async function forgotPassword(
  request: ForgotPasswordRequest,
  signal?: AbortSignal,
): Promise<CommonSuccessResponse> {
  const AccountDomainId = accountDomainId(request.AccountDomainId);
  return api.post<CommonSuccessResponse>(
    endpoints.player.forgotPassword.host,
    endpoints.player.forgotPassword.path,
    {
      Email: request.Email,
      ...(AccountDomainId !== undefined ? { AccountDomainId } : {}),
    },
    signal,
  );
}

/** Request a password-reset SMS; returns Guid for the reset form. */
export async function forgotPasswordSms(
  request: ForgotPasswordSmsRequest,
  signal?: AbortSignal,
): Promise<ForgotPasswordSmsResponse> {
  const AccountDomainId = accountDomainId(request.AccountDomainId);
  return api.post<ForgotPasswordSmsResponse>(
    endpoints.player.forgotPasswordSms.host,
    endpoints.player.forgotPasswordSms.path,
    {
      Telephone: request.Telephone,
      ...(AccountDomainId !== undefined ? { AccountDomainId } : {}),
    },
    signal,
  );
}

/** Email the player their username. */
export async function forgotUsername(
  request: ForgotUsernameRequest,
  signal?: AbortSignal,
): Promise<CommonSuccessResponse> {
  const AccountDomainId = accountDomainId(request.AccountDomainId);
  return api.post<CommonSuccessResponse>(
    endpoints.player.forgotUsername.host,
    endpoints.player.forgotUsername.path,
    {
      Email: request.Email,
      ...(AccountDomainId !== undefined ? { AccountDomainId } : {}),
    },
    signal,
  );
}

/** Set a new password using the reset Guid (and optional SMS code). */
export async function resetPassword(
  request: ResetPasswordRequest,
  signal?: AbortSignal,
): Promise<CommonSuccessResponse> {
  const body: Record<string, unknown> = {
    Guid: request.Guid,
    Password: request.Password,
    ConfirmPassword: request.ConfirmPassword,
  };
  if (request.Code) body.Code = request.Code;

  return api.post<CommonSuccessResponse>(
    endpoints.player.resetPassword.host,
    endpoints.player.resetPassword.path,
    body,
    signal,
  );
}
